import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast, ToastOptions } from 'react-toastify'

import {
  RelacaoDocumentosLicencaAluno,
  UploadDocumentos,
  UploadDocumentosCertificacao
} from 'src/compartilhados'
import { ResumoDeDocumentosMultiplosDeEntradaProfessorCertificacaoMapper } from 'src/compartilhados/modulos/gestao-de-documentos/mappers/resumo-de-documentos.mapper'
import {
  Carregando,
  ErroContainerLayout,
  ErroLayoutContainer,
  Modal,
  ModalRef
} from 'src/componentes'
import { RotasProfessor } from 'src/rotas/professor'
import { Api } from 'src/servicos'
import { Documento } from 'src/tipos'

import { ContainerDaPagina, Dicas, TituloComplementar } from './styles'

const erroAoBaixarDocumentos = 'Erro ao baixar documentos.'
const ErroAoObterDocumentos =
  'Não foi possível obter os documentos do professor.'

const toastOptions = { type: 'error' } as ToastOptions

export type DocumentosProfessorCertificacaoHandles = {
  enviarDocumentos: () => void
}

interface DocumentosProfessorCertificacaoProps {
  professorCertificacaoId: string | undefined
  documentos: UploadDocumentos[]
  atualizar: (novosDocumentos: UploadDocumentos) => void
  reiniciar: (novosDocumentos: UploadDocumentos[]) => void
}

const DocumentosProfessorCertificacao = forwardRef<
  DocumentosProfessorCertificacaoHandles,
  DocumentosProfessorCertificacaoProps
>(({ professorCertificacaoId, documentos, atualizar, reiniciar }) => {
  const history = useHistory()
  const [pronto, definirPronto] = useState<boolean>()
  const modalDeCancelamento = useRef<ModalRef>(null)

  const { limparErro, definirErro, erro } = ErroLayoutContainer.useContainer()

  const tiposLicenca = useMemo(
    () => RelacaoDocumentosLicencaAluno.map(x => x.tipo),
    [RelacaoDocumentosLicencaAluno]
  )

  const obterResumoDeDocumentos = async (idDoUsuario: string) => {
    try {
      definirPronto(false)
      limparErro()
      const resposta = await Api.ObterResumoDeDocumentosProfessorCertificacao(
        idDoUsuario
      )

      const resultado = ResumoDeDocumentosMultiplosDeEntradaProfessorCertificacaoMapper(
        documentos,
        resposta
      )

      reiniciar(resultado)
    } catch (erro) {
      const mensagens = ErroAoObterDocumentos
      const acaoVoltar = () => history.goBack()
      definirErro({ mensagens, acaoVoltar })
    } finally {
      definirPronto(true)
    }
  }

  const abrirImagemNovaAba = (documentoProfessor: Documento) => {
    const novaAba = window.open('')
    if (documentoProfessor.contentType === 'application/pdf') {
      novaAba.document.write(
        `<iframe width='100%' height='100%' src='data:${documentoProfessor?.contentType};base64, ` +
          encodeURI(documentoProfessor?.base64) +
          "' />"
      )
    } else {
      const img = `<img src='${`data:${documentoProfessor?.contentType};base64, ${documentoProfessor?.base64}`}' />`
      novaAba.document.write(img)
    }
    novaAba.document.title = 'Documento do professor'
  }

  const visualizarDocumento = async (documentoId: string) => {
    try {
      const novoDocumento = await Api.RequisitarDocumentoProfessorCertificacao(
        professorCertificacaoId,
        documentoId
      )
      if (novoDocumento) {
        abrirImagemNovaAba(novoDocumento)
      }
    } catch (error) {
      toast(ErroAoObterDocumentos, toastOptions)
    }
  }

  const excluirDocumento = async (documentoId: string) => {
    try {
      await Api.RemoverDocumentoProfessorCertificacao(
        professorCertificacaoId,
        documentoId
      )
      reiniciar(documentos.filter(doc => doc.id !== documentoId))
    } catch (error) {
      toast(ErroAoObterDocumentos, toastOptions)
    }
  }

  const obterDownloadDeDocumentos = async (
    documentoId: string
  ): Promise<void> => {
    try {
      await Api.DownloadDocumentosProfessorCertificacao(
        professorCertificacaoId,
        documentoId
      )
    } catch (erro) {
      toast(erroAoBaixarDocumentos, toastOptions)
    }
  }

  const documentosProfessor = useMemo(() => {
    return documentos.filter(x => !tiposLicenca.some(y => y === x.tipo))
  }, [documentos])

  useEffect(() => {
    if (professorCertificacaoId) {
      obterResumoDeDocumentos(professorCertificacaoId)
    }
    definirPronto(true)
  }, [])

  return pronto && !erro ? (
    <ContainerDaPagina>
      {documentosProfessor.map(documento => (
        <UploadDocumentosCertificacao
          key={documento.id}
          documento={documento}
          eventoDeMudanca={atualizar}
          eventoDeDownload={obterDownloadDeDocumentos}
          eventoDeVisualizacao={visualizarDocumento}
          eventoDeExclusao={excluirDocumento}
        />
      ))}
      <Modal
        ref={modalDeCancelamento}
        backdrop
        id="modal-de-cancelamento"
        titulo={'Deseja cancelar?'}
        acaoPrimaria={{
          titulo: 'Sim',
          tipo: 'button',
          acao: () => history.push(RotasProfessor.Dashboard)
        }}
        acaoSecundario={{
          titulo: 'Não',
          tipo: 'button',
          acao: () => modalDeCancelamento?.current?.fechar()
        }}
      >
        <p>Selecione uma opção</p>
      </Modal>
    </ContainerDaPagina>
  ) : !pronto && !erro ? (
    <Carregando texto="Carregando documentos do professor..." />
  ) : (
    <ErroContainerLayout />
  )
})

export default DocumentosProfessorCertificacao
